import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { IntivivLogoMark } from '../components/IntivivLogo';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Affirmations() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [activeGroup, setActiveGroup] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/affirmations`)
      .then(r => {
        const gs = r.data.groups || [];
        setGroups(gs);
        if (gs.length) setActiveGroup(gs[0].id);
      })
      .catch(() => toast.error('Could not load affirmations.'));
  }, []);

  const current = groups.find(g => g.id === activeGroup);

  const copyOne = async (text) => {
    try {
      if (navigator.clipboard) await navigator.clipboard.writeText(text);
      toast.success('Copied. Read it out loud — slowly.');
    } catch { toast.error('Could not copy.'); }
  };

  const shareWhatsApp = (text) => {
    const url = `https://wa.me/?text=${encodeURIComponent('"' + text + '"\n— via INTIVIV')}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div style={{ minHeight: '100vh', paddingBottom: 'var(--bottom-h)', position: 'relative' }} data-testid="affirmations-page">
      <div className="education-aurora" aria-hidden="true" />

      <div style={{ padding: '18px 16px 8px', position: 'relative', zIndex: 1 }}>
        <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', fontSize: '0.79rem', letterSpacing: '0.15em', padding: 0, marginBottom: 10 }} data-testid="affirmations-back">
          <i className="fa-solid fa-arrow-left" /> BACK
        </button>

        {/* Brand mark — centered, glowing halo for a "title-page" feel */}
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 4, marginBottom: 10 }}>
          <motion.div
            initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.6, ease: 'easeOut' }}
            style={{
              position: 'relative',
              width: 96, height: 96,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {/* Soft gold halo */}
            <div style={{
              position: 'absolute', inset: -10, borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(212,175,55,0.18) 0%, transparent 65%)',
              filter: 'blur(8px)',
              pointerEvents: 'none',
            }} />
            {/* Rotating ring */}
            <div style={{
              position: 'absolute', inset: -4, borderRadius: '50%',
              border: '1px solid rgba(212,175,55,0.35)',
              animation: 'ringRotate 18s linear infinite',
              pointerEvents: 'none',
            }} />
            <IntivivLogoMark width={68} />
          </motion.div>
        </div>

        <div className="eyebrow" style={{ textAlign: 'center' }}>Read out loud · slowly · daily</div>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: '2.2rem', marginBottom: 4, textAlign: 'center' }}><span className="gold-italic">Affirmations</span></div>
        <p style={{ fontFamily: 'var(--font-ui)', fontSize: '0.87rem', color: 'var(--text-muted)', marginBottom: 14, lineHeight: 1.55, textAlign: 'center' }}>
          Six themed sets — for the body, worth, the relationship, desire, healing and power. Choose one in the morning. Return to it before sleep.
        </p>
      </div>

      {/* Group tabs (3 cols × 2 rows) */}
      <div style={{ padding: '0 16px 14px', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, position: 'relative', zIndex: 1 }} data-testid="affirmations-group-tabs">
        {groups.map(g => (
          <button
            key={g.id}
            onClick={() => setActiveGroup(g.id)}
            data-testid={`affirmations-group-${g.id}`}
            style={{
              padding: '10px 6px',
              fontSize: '0.71rem',
              letterSpacing: '0.06em',
              fontFamily: 'var(--font-ui)',
              background: activeGroup === g.id ? `${g.color}28` : 'var(--bg2)',
              color: activeGroup === g.id ? g.color : 'var(--text-muted)',
              border: `1px solid ${activeGroup === g.id ? g.color : 'var(--border-soft)'}`,
              cursor: 'pointer',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
              minHeight: 64, textAlign: 'center', lineHeight: 1.18,
              transition: 'all 0.18s ease',
            }}
          >
            <i className={`fa-solid ${g.icon}`} style={{ fontSize: '1.03rem' }} />
            {g.label}
          </button>
        ))}
      </div>

      {/* Affirmation cards */}
      <div style={{ padding: '0 16px 0', position: 'relative', zIndex: 1, display: 'grid', gap: 10 }}>
        <AnimatePresence mode="wait">
          {current?.items.map((text, idx) => (
            <motion.div
              key={`${current.id}-${idx}`}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.045 }}
              style={{
                position: 'relative',
                padding: '20px 18px 16px',
                background: 'var(--bg2)',
                border: `1px solid ${current.color}3a`,
                borderLeft: `3px solid ${current.color}`,
                overflow: 'hidden',
              }}
              data-testid={`affirmation-${idx}`}
            >
              <i className="fa-solid fa-quote-left" style={{ position: 'absolute', top: 10, left: 12, color: `${current.color}44`, fontSize: '1.1rem' }} />
              <p style={{
                fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic',
                fontSize: '1.2rem', lineHeight: 1.55, color: 'var(--text)',
                marginLeft: 18, marginBottom: 14,
              }}>
                {text}
              </p>
              <div style={{ display: 'flex', gap: 14, justifyContent: 'flex-end', marginLeft: 18 }}>
                <button onClick={() => copyOne(text)} title="Copy" aria-label="Copy" style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', fontSize: '0.79rem' }} data-testid={`affirmation-copy-${idx}`}>
                  <i className="fa-regular fa-copy" /> COPY
                </button>
                <button onClick={() => shareWhatsApp(text)} title="Share on WhatsApp" aria-label="Share" style={{ background: 'none', border: 'none', color: current.color, cursor: 'pointer', fontSize: '0.79rem' }} data-testid={`affirmation-share-${idx}`}>
                  <i className="fa-brands fa-whatsapp" /> SHARE
                </button>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}
